import { ThemedFixedButton } from "@/Button";
import { ValidateCheckbox } from "@/Checkbox";
import { Divider } from "@/Divider";
import { Header } from "@/Header";
import { ValidateInput } from "@/Input";
// import { SequentialAnimation } from "@/SequentialAnimation";
import { Dialog } from "@/Dialog";
import { useAppColor } from "^/common/useAppColor";
import { useStorage } from "^/common/useStorage";
import { SignType } from "#/unique/domain";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useContext, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Controller, useForm } from "react-hook-form";
import { SignUpBusinessState } from "./business-owner";
import BusinessAddressPicker from "@/common/BusinessAddressPicker";
import { SignupContext } from "@/SignupContext";
import {
  useBusinessLoginSelectList,
  useBusinessSignUpWithFile,
  businessAppLogin,
} from "~/services/auth";
import { ValidateSelectList } from "@/SelectList";
import { transformKeyValueArrayToOptions } from "%/dataTransform";
import { ValidateControllerSelect } from "@/ControllerSelect";
import { SHA256 } from "crypto-js";

export const Route = createFileRoute("/sign-up/business-owner-detail")({
  component: RouteComponent,
});

export interface SignUpBusinessDetailState extends SignUpBusinessState {
  agree3: boolean;
  storeTelNumber: string;
  storeAddress: string;
  storeAddressDetail: string;
  mainBank: string;
  mainAccountNumber: string;
  playgroundYn: string;
  facilities: string;
  weightClasses: string;
  serviceProvides: string;
  mainAccountName: string;
  longitude: string;
  latitude: string;
  maxWeight: number;
}

function RouteComponent() {
  const [cookie] = useCookies(["email"]);
  const [snsId] = useStorage<string>("local-sns-id");
  const [snsType] = useStorage<SignType>("local-sign-sns-type");
  const [fcm] = useStorage<string>("session-fcm");
  const { getInfo, setInfo } = useContext(SignupContext);
  const email: string = useMemo(() => cookie.email, [cookie]);
  const { history } = useRouter();
  const { mutate: signUp, isPending } = useBusinessSignUpWithFile();
  const submittingRef = useRef(false); // 중복 제출 가드(연타 방지)

  // Dialog 상태 관리
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const {
    handleSubmit,
    control,
    setValue,
    register,
    watch,
    formState: { errors },
  } = useForm<SignUpBusinessDetailState>({
    defaultValues: {
      agree1: getInfo()?.agree1,
      allAlamYn: getInfo()?.allAlamYn,
      email: getInfo()?.email,
      storeName: getInfo()?.storeName,
      storeNumber: getInfo()?.storeNumber,
      businessLicense: getInfo()?.businessLicense,
      name: getInfo()?.name,
      phone: getInfo()?.phone,
      maxWeight: getInfo()?.maxWeight,
    },
    // 제출(가입 클릭) 시 전체 검증 → 필수란 아래 에러 표시.
    // 재입력하면 자동으로 다시 검증(reValidateMode 기본 onChange)돼 에러가 사라진다.
    mode: "onSubmit",
  });

  // 빈칸 제거 함수
  const handleTrimOnBlur = (fieldName: keyof SignUpBusinessDetailState) => {
    const currentValue = watch(fieldName);
    if (typeof currentValue === "string" && currentValue.endsWith(" ")) {
      setValue(fieldName, currentValue.trim(), { shouldValidate: true });
    }
  };
  const { data: selectList } = useBusinessLoginSelectList();
  const bindSubmit = (data: SignUpBusinessDetailState) => {
    // 이미 제출 처리 중이면 무시(연타/딜레이로 인한 중복 가입 방지)
    if (isPending || submittingRef.current) return;
    submittingRef.current = true;
    // Context에 최종 데이터 저장

    // 1단계에서 업로드된 파일 정보 가져오기
    const businessLicenseFile = getInfo()?.businessLicense;
    // 앱(네이티브 브릿지)에서 사본을 먼저 올린 경우의 fileSeq
    const businessFileSeq = getInfo()?.businessFileSeq as number | undefined;

    if (!businessLicenseFile && !businessFileSeq) {
      console.error("사업자 등록증 파일이 없습니다.");
      submittingRef.current = false; // 진행 못 하므로 가드 해제
      return;
    }

    // API 요청 데이터 준비 (파일 또는 fileSeq)
    const contextInfo = getInfo();
    const signUpData = {
      // 1단계 데이터
      email: data.email || email || "",
      storeName: contextInfo?.storeName || "",
      storeNumber: contextInfo?.storeNumber?.toString() || "",
      // 웹: File, 앱: 생략(businessFileSeq 로 전달)
      businessLicense: businessFileSeq ? undefined : (businessLicenseFile as File),
      businessFileSeq: businessFileSeq,
      name: contextInfo?.name || "",
      phone: contextInfo?.phone || "",
      agree1: contextInfo?.agree1 ?? false,
      allAlamYn: contextInfo?.allAlamYn ? "Y" : "N",

      // 2단계 데이터
      agree3: data.agree3 ?? false,
      storeTelNumber: data.storeTelNumber || "",
      storeAddress: data.storeAddress || "",
      storeAddressDetail: data.storeAddressDetail || "",
      mainBank: data.mainBank || "",
      mainAccountNumber: data.mainAccountNumber || "",
      mainAccountName: (data.mainAccountName || "").trim(),
      playgroundYn: data.playgroundYn,
      facilities: data.facilities.split(",") || [],
      // weightClasses: data.weightClasses.split(",") || [],
      serviceProvides: data.serviceProvides.split(",") || [],
      maxWeight: data.maxWeight,
      // SNS 정보
      snsId: snsId || "",
      snsType: snsType || "",
      longitude: data.longitude || "",
      latitude: data.latitude || "",
      salesPartners: "",
    };

    // console.log(signUpData);
    setInfo(signUpData);
    // 비즈니스 회원가입 API 호출 (파일 포함)
    signUp(signUpData, {
      onSuccess: async (response) => {
        console.log("회원가입 성공:", response);
        const userAgent = navigator.userAgent;
        const isApp = /MT_I|MT_A/i.test(userAgent);
        if (response.statusCode === 200) {
          if (isApp) {
            // 회원가입 성공 후 앱 로그인 API 호출
            const id = snsId || "";
            const key = snsType + id;
            const hash = SHA256(key).toString();
            try {
              const appLoginData = {
                cdSnsType: snsType || "",
                hash: hash,
                fcmToken: fcm || "",
                cdMobileType: /MT_A/i.test(userAgent) ? "ANDROID" : "IOS",
              };
              console.log("==================", JSON.stringify(appLoginData));
              const loginResponse = await businessAppLogin(appLoginData);
              console.log("앱 로그인 성공:", loginResponse);
              console.log("==================", JSON.stringify(loginResponse));
              // 앱 로그인 성공 시 조건에 따른 리다이렉트
              // 앱 로그인 성공 시 조건에 따른 리다이렉트
              if (loginResponse?.statusCode === 200) {
                // 로그인 성공 시 비즈니스 메인 페이지로 이동
                history.push("/sign-up/business-owner-done");
              } else {
                // 로그인 실패 시 회원가입 완료 페이지로 이동
                history.push("/");
              }
            } catch (error) {
              console.error("앱 로그인 실패:", error);
              submittingRef.current = false; // 중복가드 해제(버튼 잠김 방지)
              // 앱 로그인이 실패해도 회원가입은 성공했으므로 다음 페이지로 이동
              // history.push("/sign-up/business-owner-done");
            }
          } else {
            history.push("/sign-up/business-owner-done");
          }
        } else {
          submittingRef.current = false; // 서버가 실패(비200) 반환 → 재시도 가능
          // 서버에서 받은 메시지를 Dialog로 표시
          setDialogMessage(response.message);
          setShowDialog(true);
        }
      },
      onError: (error: any) => {
        console.error("회원가입 실패:", error);
        submittingRef.current = false; // 실패 시 재시도 가능하도록 가드 해제
        // 서버에서 받은 에러 메시지를 Dialog로 표시
        const errorMessage =
          error?.response?.data?.message ||
          "회원가입에 실패했습니다. 다시 시도해주세요.";
        setDialogMessage(errorMessage);
        setShowDialog(true);
      },
    });
  };

  useAppColor({
    background: "#ff9934",
  });

  return (
    <div className="flex flex-col flex-auto">
      <Header title="회원가입" className="text-white border-0 bg-yellow" />
      <form className="bg-white flex flex-col flex-auto rounded-t-3xl pt-10 px-5  gap-4 min-h-[calc(100vh-100px)]">
        {/* <SequentialAnimation> */}
        <ValidateInput
          className="w-full"
          labelClassName="mb-0"
          placeholder="이메일 주소를 입력하세요"
          label="이메일 주소"
          value={email}
          disabled
        />
        <ValidateInput
          className="w-full"
          labelClassName="mb-0"
          label="상호명"
          placeholder="상호명을 입력하세요"
          value={getInfo()?.storeName}
          disabled
        />
        <ValidateInput
          className="w-full"
          labelClassName="mb-0"
          label="사업자번호"
          value={getInfo()?.storeNumber}
          disabled
        />
        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium text-gray-700 mb-3 mt-0.5">
            사업장 주소
          </label>
          <Controller
            name="storeAddress"
            control={control}
            rules={{ required: "주소를 검색해주세요." }}
            render={({ field }) => (
              <BusinessAddressPicker
                value={field.value}
                onSelect={(picked) => {
                  // 주소·좌표는 항상 한 번에 갱신되어 불일치가 발생하지 않는다
                  setValue("storeAddress", picked.address, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue("longitude", picked.longitude, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue("latitude", picked.latitude, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            )}
          />
          {errors.storeAddress && (
            <p className="-mt-1 text-sm text-red-500">
              {errors.storeAddress.message}
            </p>
          )}
          <ValidateInput
            // name="storeAddressDetail"
            className="w-full"
            placeholder="상세주소를 입력하세요"
            // errorMessage={errors?.storeAddressDetail?.message}
            {...register("storeAddressDetail", {
              // required: { value: true, message: "상세주소를 입력하세요." },
            })}
            onBlur={() => handleTrimOnBlur("storeAddressDetail")}
          />
        </div>
        <ValidateInput
          className="w-full"
          label="사업장 대표번호"
          labelClassName="mb-0"
          type="tel"
          errorMessage={errors?.storeTelNumber?.message}
          {...register("storeTelNumber", {
            // required: { value: true, message: "휴대폰 번호를 입력하세요" },
            // pattern: {
            //   value: /^\d{3}-\d{3,4}-\d{4}$/,
            //   message: "유효한 휴대폰 번호를 입력하세요",
            // },
          })}
          onBlur={() => handleTrimOnBlur("storeTelNumber")}
        />
        <div className="flex flex-col gap-2">
          <Controller
            name="mainBank"
            control={control}
            // rules={{ required: "반려동물 품종을 선택하세요" }}
            render={({ field }) => (
              <ValidateSelectList
                onChange={(selectedOption) =>
                  field.onChange(selectedOption?.value || "")
                }
                label="정산 계좌 번호"
                placeholder="은행 선택"
                // required={true}
                isSearchable={false}
                // errorMessage={errors?.mainBank?.message}
                options={bankList}
              />
            )}
          />
          <ValidateInput
            className="w-full"
            placeholder="예금주"
            errorMessage={errors?.mainAccountName?.message}
            {...register("mainAccountName", {
              required: { value: true, message: "예금주를 입력하세요" },
              minLength: {
                value: 2,
                message: "예금주는 최소 2글자 이상이어야 합니다",
              },
              // 한글 + 공백 허용(상호명 예금주 대응). 앞뒤 공백은 저장 시 트림.
              pattern: {
                value: /^[가-힣\s]+$/,
                message: "예금주는 한글과 공백만 입력할 수 있어요",
              },
            })}
            onBlur={() => handleTrimOnBlur("mainAccountName")}
          />
          <ValidateInput
            className="w-full"
            placeholder="계좌번호를 입력하세요"
            // errorMessage={errors?.mainAccountNumber?.message}
            {...register("mainAccountNumber", {
              // required: { value: true, message: "계좌번호를 입력하세요" },
            })}
            onBlur={() => handleTrimOnBlur("mainAccountNumber")}
          />
        </div>
        <Controller
          name="serviceProvides"
          control={control}
          defaultValue={""}
          rules={{ required: "제공 서비스를 1개 이상 선택해주세요" }}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <ValidateControllerSelect
                value={field.value ? field.value.split(",") : []}
                label="제공 서비스 선택"
                labelClassName="mb-0"
                required={true}
                onChange={(value) => {
                  field.onChange(value.join(","));
                }}
                options={
                  selectList?.providedServices
                    ? transformKeyValueArrayToOptions(
                        selectList.providedServices,
                      )
                    : []
                }
              />
              {errors.serviceProvides && (
                <p className="text-sm text-red-500">
                  {errors.serviceProvides.message}
                </p>
              )}
            </div>
          )}
        />
        <Controller
          name="playgroundYn"
          control={control}
          defaultValue={"Y"}
          render={({ field }) => (
            <ValidateControllerSelect
              value={field.value ? field.value.split(",") : []}
              label="실외 운동장"
              labelClassName="mb-0"
              required={true}
              onChange={(value) => {
                field.onChange(value.join(","));
              }}
              max={1}
              options={[
                { label: "있어요", value: "Y" },
                { label: "없어요", value: "N" },
              ]}
            />
          )}
        />
        <Controller
          name="facilities"
          control={control}
          defaultValue={""}
          rules={{ required: "시설을 1개 이상 선택해주세요" }}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <ValidateControllerSelect
                value={field.value ? field.value.split(",") : []}
                label="시설"
                labelClassName="mb-0"
                required={true}
                onChange={(value) => {
                  field.onChange(value.join(","));
                }}
                options={
                  selectList?.facilities
                    ? transformKeyValueArrayToOptions(selectList.facilities)
                    : []
                }
              />
              {errors.facilities && (
                <p className="text-sm text-red-500">
                  {errors.facilities.message}
                </p>
              )}
            </div>
          )}
        />
        <ValidateInput
          className="w-full"
          label="수용 가능 체급"
          labelClassName="mb-0"
          placeholder="숫자로 입력하세요. (Kg)"
          errorMessage={errors?.maxWeight?.message}
          {...register("maxWeight", {
            required: { value: true, message: "수용 가능 체급을 입력하세요" },
            pattern: {
              value: /^[0-9]+$/,
              message: "숫자만 입력 가능합니다",
            },
            min: {
              value: 1,
              message: "1 이상의 숫자를 입력하세요",
            },
            max: {
              value: 100,
              message: "100 이하의 숫자를 입력하세요",
            },
          })}
        />
        {/* <Controller
          name="weightClasses"
          control={control}
          defaultValue={""}
          render={({ field }) => (
            <ValidateControllerSelect
              value={field.value ? field.value.split(",") : []}
              label="수용 가능 체급"
              required={true}
              onChange={(value) => {
                field.onChange(value.join(","));
              }}
              options={
                selectList?.grades
                  ? transformKeyValueArrayToOptions(selectList.grades)
                  : []
              }
            />
          )}
        /> */}
        {/* </SequentialAnimation> */}
        <Divider className="mt-auto" />

        {/* <SequentialAnimation initialDelay={0.2}> */}
        <Divider line />
        <Controller
          name="agree3"
          control={control}
          rules={{ required: "필수 동의입니다." }}
          render={({ field }) => (
            <ValidateCheckbox
              defaultChecked={field.value}
              onChange={() => field.onChange(!field.value)}
              errorMessage={errors?.agree3?.message}
              title="[필수]사업자 정보 및 제공 서비스에 틀림이 없음을 확인합니다."
            />
          )}
        />
        <div className="pb-5 mb-2"></div>
        {/* </SequentialAnimation> */}
      </form>

      <ThemedFixedButton
        disabled={isPending}
        onClick={handleSubmit(bindSubmit, () => {
          // 검증 실패 시 첫 에러 항목으로 스크롤(렌더 후)
          setTimeout(() => {
            document
              .querySelector(".text-red-500")
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 50);
        })}
        color="primary"
        size="md"
      >
        {isPending ? "가입 중..." : "약관 동의하고 가입하기"}
      </ThemedFixedButton>

      {/* Dialog 컴포넌트 */}
      <Dialog
        visible={showDialog}
        onClose={() => {
          setShowDialog(false);
          history.push("/");
        }}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">알림</h3>
          <p className="mb-4 text-gray-600">{dialogMessage}</p>
          <button
            onClick={() => {
              setShowDialog(false);
              history.push("/");
            }}
            className="px-4 py-2 font-medium text-white bg-orange-500 rounded-lg"
          >
            확인
          </button>
        </div>
      </Dialog>
    </div>
  );
}

const bankList = [
  { label: "카카오뱅크", value: "카카오뱅크" },
  { label: "국민은행", value: "국민은행" },
  { label: "기업은행", value: "기업은행" },
  { label: "농협은행", value: "농협은행" },
  { label: "신한은행", value: "신한은행" },
  { label: "iM뱅크", value: "iM뱅크" },
  { label: "산업은행", value: "산업은행" },
  { label: "우리은행", value: "우리은행" },
  { label: "한국씨티은행", value: "한국씨티은행" },
  { label: "하나은행", value: "하나은행" },
  { label: "SC제일은행", value: "SC제일은행" },
  { label: "경남은행", value: "경남은행" },
  { label: "광주은행", value: "광주은행" },
  { label: "도이치은행", value: "도이치은행" },
  { label: "뱅크오브아메리카", value: "뱅크오브아메리카" },
  { label: "부산은행", value: "부산은행" },
  { label: "산림조합중앙회", value: "산림조합중앙회" },
  { label: "저축은행", value: "저축은행" },
  { label: "새마을금고", value: "새마을금고" },
  { label: "수협은행", value: "수협은행" },
  { label: "신협중앙회", value: "신협중앙회" },
  { label: "우체국", value: "우체국" },
  { label: "전북은행", value: "전북은행" },
  { label: "제주은행", value: "제주은행" },
  { label: "중국건설은행", value: "중국건설은행" },
  { label: "중국공상은행", value: "중국공상은행" },
  { label: "중국은행", value: "중국은행" },
  { label: "BNP파리바은행", value: "BNP파리바은행" },
  { label: "HSBC은행", value: "HSBC은행" },
  { label: "JP모간체이스은행", value: "JP모간체이스은행" },
  { label: "케이뱅크", value: "케이뱅크" },
  { label: "토스뱅크", value: "토스뱅크" },
];
